import React, { useState, useEffect, useRef } from 'react';
import { supabase, registerPushSubscription, VAPID_PUBLIC_KEY } from './supabase';
import './App.css';

export default function App() {
  const [pingCount, setPingCount] = useState(0);
  const [notes, setNotes] = useState([]);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState('');
  const [isMom, setIsMom] = useState(false);
  const [notifStatus, setNotifStatus] = useState('idle'); // idle | asking | granted | denied
  const [floaters, setFloaters] = useState([]);
  const floaterIdRef = useRef(0);
  const toastTimer = useRef(null);

  useEffect(() => {
    registerSW();
    loadData();
    const channel = supabase
      .channel('realtime-updates')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'pings' }, () => {
        setPingCount(c => c + 1);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notes' }, payload => {
        setNotes(prev => [payload.new, ...prev]);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  async function registerSW() {
    if ('serviceWorker' in navigator) {
      await navigator.serviceWorker.register('/sw.js');
    }
  }

  async function loadData() {
    const [{ count }, { data: notesData }] = await Promise.all([
      supabase.from('pings').select('*', { count: 'exact', head: true }),
      supabase.from('notes').select('*').order('created_at', { ascending: false }).limit(50),
    ]);
    setPingCount(count || 0);
    setNotes(notesData || []);
  }

  async function handlePing() {
    spawnHearts();
    await supabase.from('pings').insert({});
    // trigger push via edge function
    await supabase.functions.invoke('send-push', {
      body: { type: 'ping' },
    });
  }

  function spawnHearts() {
    const newFloaters = Array.from({ length: 6 }, (_, i) => ({
      id: floaterIdRef.current++,
      left: 20 + Math.random() * 60,
      delay: i * 80,
      size: 10 + Math.random() * 10,
    }));
    setFloaters(prev => [...prev, ...newFloaters]);
    setTimeout(() => {
      setFloaters(prev => prev.filter(f => !newFloaters.find(n => n.id === f.id)));
    }, 1500);
  }

  async function handleNote(e) {
    e.preventDefault();
    if (!name.trim()) { showToast('add your name first'); return; }
    if (!message.trim()) { showToast('write her something'); return; }
    await supabase.from('notes').insert({ from_name: name.trim(), message: message.trim() });
    await supabase.functions.invoke('send-push', {
      body: { type: 'note', from: name.trim() },
    });
    setName('');
    setMessage('');
    showToast('saved with love ♥');
  }

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2800);
  }

  async function enableNotifications() {
    setNotifStatus('asking');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setNotifStatus('denied'); return; }
      const sub = await registerPushSubscription();
      if (sub) {
        await supabase.from('push_subscriptions').upsert({
          endpoint: sub.endpoint,
          subscription: JSON.stringify(sub),
        }, { onConflict: 'endpoint' });
        setNotifStatus('granted');
        showToast('notifications on! 🎉');
      }
    } catch (err) {
      setNotifStatus('denied');
    }
  }

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  return (
    <div className="app">
      <header className="header">
        <h1>Thinking of you, Rasha</h1>
        <p>tap the heart whenever she crosses your mind</p>
      </header>

      {/* Mom's notification button */}
      <div className="mom-bar">
        {!isMom ? (
          <button className="link-btn" onClick={() => setIsMom(true)}>
            Are you Rasha? Tap here →
          </button>
        ) : notifStatus === 'granted' ? (
          <p className="notif-on">you'll be notified when someone thinks of you 💗</p>
        ) : (
          <button className="notif-btn" onClick={enableNotifications} disabled={notifStatus === 'asking'}>
            {notifStatus === 'asking' ? 'enabling…' : notifStatus === 'denied' ? 'notifications blocked in browser settings' : 'enable notifications so you feel the love'}
          </button>
        )}
      </div>

      {/* Ping section */}
      <section className="ping-section">
        <div className="ping-counter">
          <span>{pingCount.toLocaleString()}</span>
          people have thought of her
        </div>
        <div className="heart-wrap">
          <button className="heart-btn" onClick={handlePing} aria-label="Send a thinking of you ping">
            <svg viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M45 75 C45 75 12 55 12 33 C12 22 20 14 30 14 C37 14 43 18 45 22 C47 18 53 14 60 14 C70 14 78 22 78 33 C78 55 45 75 45 75Z"
                fill="#f5cee0" stroke="#c97ba0" strokeWidth="1.5"
              />
            </svg>
          </button>
          {floaters.map(f => (
            <span
              key={f.id}
              className="floater"
              style={{ left: `${f.left}%`, fontSize: `${f.size}px`, animationDelay: `${f.delay}ms` }}
            >♥</span>
          ))}
        </div>
        <p className="ping-label">anonymous · she'll feel the love</p>
      </section>

      <hr className="divider" />

      {/* Note form */}
      <section className="note-section">
        <h2>leave her a note</h2>
        <form onSubmit={handleNote} className="note-form">
          <input
            type="text"
            placeholder="your name"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={40}
          />
          <textarea
            placeholder="what were you thinking about her?"
            value={message}
            onChange={e => setMessage(e.target.value)}
            maxLength={500}
          />
          <button type="submit" className="submit-btn">save this memory</button>
          {toast && <p className="toast">{toast}</p>}
        </form>
      </section>

      <hr className="divider" />

      {/* Notes wall */}
      <section className="wall-section">
        <h2>her memory wall</h2>
        {notes.length === 0 ? (
          <p className="empty">notes left for her will appear here</p>
        ) : (
          <div className="notes-list">
            {notes.map(note => (
              <div key={note.id} className="note-card">
                <p className="note-from">from {note.from_name}</p>
                <p className="note-text">{note.message}</p>
                <p className="note-time">{formatDate(note.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
